import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Breadcrumb } from 'react-bootstrap';
import SEOHead from '../components/SEOHead';
import { contactInfo } from '../data/businessData';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 5000);
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <>
      <SEOHead page="contact" />
      
      {/* Breadcrumb */}
      <Container className="py-3">
        <Breadcrumb>
          <Breadcrumb.Item href="#home">Home</Breadcrumb.Item>
          <Breadcrumb.Item active>Contact Us</Breadcrumb.Item>
        </Breadcrumb>
      </Container>

      {/* Contact Header */}
      <section className="py-5 bg-primary text-white">
        <Container>
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-3">Contact Laxmi Tyre & Motors</h1>
            <p className="lead mb-0">
              Get in touch for expert car service in Indore | Book your appointment today
            </p>
          </div>
        </Container>
      </section>

      {/* Contact Information */}
      <section className="py-5">
        <Container>
          <Row>
            {/* Contact Details */}
            <Col lg={6} className="mb-4">
              <h2 className="h3 fw-bold text-dark mb-4">Get In Touch</h2>
              <p className="text-muted mb-4">
                Ready to service your vehicle? Contact Laxmi Tyre & Motors today for professional 
                automobile care in Indore. We're here to help with all your car service needs.
              </p>

              {/* Contact Cards */}
              <div className="mb-4">
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <div className="bg-primary text-white rounded-circle p-3 me-3">
                        📞
                      </div>
                      <div>
                        <h5 className="fw-bold mb-1">Phone Number</h5>
                        <p className="text-muted mb-1">{contactInfo.phone}</p>
                        <small className="text-success">Call us anytime for immediate assistance</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div className="mb-4">
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <div className="bg-success text-white rounded-circle p-3 me-3">
                        ✉️
                      </div>
                      <div>
                        <h5 className="fw-bold mb-1">Email Address</h5>
                        <p className="text-muted mb-1">{contactInfo.email}</p>
                        <small className="text-success">We reply within 24 hours</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div className="mb-4">
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <div className="bg-warning text-white rounded-circle p-3 me-3">
                        📍
                      </div>
                      <div>
                        <h5 className="fw-bold mb-1">Our Location</h5>
                        <p className="text-muted mb-1">{contactInfo.address}</p>
                        <small className="text-success">Easy to find location in Limbodi</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div className="mb-4">
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <div className="bg-info text-white rounded-circle p-3 me-3">
                        🕒
                      </div>
                      <div>
                        <h5 className="fw-bold mb-1">Working Hours</h5>
                        <p className="text-muted mb-1">{contactInfo.hours}</p>
                        <small className="text-success">Extended hours available for emergencies</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              {/* Emergency Service */}
              <Card className="bg-danger text-white border-0">
                <Card.Body className="text-center">
                  <h5 className="fw-bold mb-2">🚨 24/7 Emergency Service</h5>
                  <p className="mb-2">Breakdown assistance available round the clock</p>
                  <div className="fw-bold fs-5">{contactInfo.phone}</div>
                </Card.Body>
              </Card>
            </Col>

            {/* Contact Form */}
            <Col lg={6}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-primary text-white">
                  <h3 className="mb-0">Send Us a Message</h3>
                </Card.Header>
                <Card.Body>
                  {showSuccess && (
                    <Alert variant="success" className="mb-4">
                      <h5 className="fw-bold mb-2">Message Sent Successfully! 🎉</h5>
                      <p className="mb-0">Thank you for contacting us. We'll get back to you within 24 hours.</p>
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Full Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Enter your full name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Phone Number *</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="Enter your phone number"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email address"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Subject *</Form.Label>
                      <Form.Select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select a subject</option>
                        <option value="Service Inquiry">Service Inquiry</option>
                        <option value="Booking Appointment">Booking Appointment</option>
                        <option value="Price Quote">Price Quote</option>
                        <option value="Complaint">Complaint</option>
                        <option value="Feedback">Feedback</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">Message *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your car service needs, issues, or questions..."
                      />
                    </Form.Group>

                    <Button variant="primary" type="submit" size="lg" className="w-100 fw-bold">
                      Send Message
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Map and Additional Info */}
      <section className="py-5 bg-light">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <div className="text-center mb-4">
                <h2 className="h3 fw-bold text-dark mb-3">Find Our Service Center</h2>
                <p className="text-muted">
                  Located in the heart of Limbodi, Indore - Easy to reach from all parts of the city
                </p>
              </div>

              {/* Map Placeholder */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                  <div 
                    className="bg-light d-flex align-items-center justify-content-center text-muted"
                    style={{ height: '400px', borderRadius: '0.375rem' }}
                  >
                    <div className="text-center">
                      <div className="display-4 mb-3">🗺️</div>
                      <h4>Interactive Map</h4>
                      <p className="mb-2">Laxmi Tyre & Motors</p>
                      <p className="mb-2">{contactInfo.address}</p>
                      <small className="text-primary">Click to open in Google Maps</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Quick Info Cards */}
              <Row>
                <Col md={4} className="mb-3">
                  <Card className="bg-primary text-white border-0 text-center h-100">
                    <Card.Body>
                      <div className="display-6 mb-2">🚗</div>
                      <h6 className="fw-bold">Free Pickup & Drop</h6>
                      <small>Service available within 5km radius</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} className="mb-3">
                  <Card className="bg-success text-white border-0 text-center h-100">
                    <Card.Body>
                      <div className="display-6 mb-2">⚡</div>
                      <h6 className="fw-bold">Same Day Service</h6>
                      <small>Most repairs completed within hours</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} className="mb-3">
                  <Card className="bg-warning text-dark border-0 text-center h-100">
                    <Card.Body>
                      <div className="display-6 mb-2">💳</div>
                      <h6 className="fw-bold">Multiple Payment</h6>
                      <small>Cash, Card, UPI accepted</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="h3 fw-bold text-dark mb-3">Frequently Asked Questions</h2>
            <p className="text-muted">
              Common questions about our car service in Indore
            </p>
          </div>

          <Row>
            <Col lg={8} className="mx-auto">
              <div className="accordion" id="faqAccordion">
                <Card className="mb-3">
                  <Card.Header>
                    <h5 className="mb-0">
                      <Button variant="link" className="text-decoration-none fw-bold">
                        How do I book a service appointment?
                      </Button>
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    You can book an appointment by calling us at {contactInfo.phone}, sending an email to {contactInfo.email}, or using our online booking form.
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <h5 className="mb-0">
                      <Button variant="link" className="text-decoration-none fw-bold">
                        Do you provide pickup and drop service?
                      </Button>
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    Yes, we offer free pickup and drop service within a 5km radius of our service center in Limbodi, Indore.
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <h5 className="mb-0">
                      <Button variant="link" className="text-decoration-none fw-bold">
                        What payment methods do you accept?
                      </Button>
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    We accept cash, credit/debit cards, and UPI payments for your convenience.
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default ContactPage;